import { useState } from 'react';
import { payAPI } from '../../../../../services/redwolfSdkAPI';
import { openNotification } from '../../../../../store/commonSlice';
import { useDispatch } from 'react-redux';
import { notification } from '../../../../../utils/constants';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';

import * as Sentry from '@sentry/react';

import Modal from '../../../../common/components/Modal';
import Spinner from '../../../../common/components/Spinner';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';
import useMobileView from '../../../../common/hooks/useMobileView';

interface deleteCardProps {
	openDeleteCardModal: boolean;
	selectedCard: any;
	closeModal: () => void;
	getCreditCards: () => void;
	openUpdateCardModal: () => void;
}

const DeleteCard = ({
	openDeleteCardModal,
	selectedCard,
	closeModal,
	getCreditCards,
	openUpdateCardModal
}: deleteCardProps) => {
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const dispatch = useDispatch();
	const [isMobileView] = useMobileView();
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Delete Card' });

	const backToUpdateCardModal = () => {
		closeModal();
		openUpdateCardModal();
	};

	const deleteCard = (event: any) => {
		event.preventDefault();
		setIsSpinnerLoading(true);

		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CONFIRM_CC_DELETION');

		requestRedwolfService(payAPI.deleteCreditCard(token, selectedCard.cardToken))
			.then((deleteCardResponse: any) => {
				setIsSpinnerLoading(false);

				closeModal();

				if (deleteCardResponse.constructor.$metadata$.simpleName === 'Success') {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CC_DELETED');

					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: notification.message.deleteCreditCardSuccess
						})
					);

					getCreditCards();
				} else if (deleteCardResponse.message) {
					Sentry.captureException(new Error('deleteCreditCard:' + deleteCardResponse.message));

					dispatch(
						openNotification({ type: 'ERROR', heading: 'Error', body: deleteCardResponse.message })
					);
				} else {
					if (deleteCardResponse.response && deleteCardResponse.response.status !== 400) {
						Sentry.captureException(new Error('deleteCreditCard:' + deleteCardResponse.response));
					}

					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: deleteCardResponse.response.detail
						})
					);
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				Sentry.captureException(err);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_DELETE_CC_ERROR', { error: err.message });
			});
	};

	return (
		<>
			{/* Delete card Modal Popup Start */}
			<Modal
				containerId="deleteCardPopup"
				show={openDeleteCardModal}
				onHide={closeModal}
				heading="Delete Card"
				containerClasses="deleteCard"
				overlaycontainerClass="deletecard-popup">
				<form className="edit-modal-form">
					<p className="popup-subtitle delete-subtitle deletecard-subtitle" id="deletesubtitle">
						{' '}
						Are you sure you would like to delete this payment method?{' '}
					</p>

					<div className="button-field-group deletebtn-group">
						{!isMobileView && (
							<button
								type="submit"
								className="secondary-button desktop-btn"
								onClick={backToUpdateCardModal}>
								Back
							</button>
						)}
						<button type="submit" className="primary-button delete-btn" onClick={deleteCard}>
							Delete Card
						</button>
						{isMobileView && (
							<button
								type="submit"
								className="secondary-button mobile-btn"
								onClick={backToUpdateCardModal}>
								Back
							</button>
						)}
					</div>
				</form>
			</Modal>
			{/* Delete card Modal Popup end */}

			{isSpinnerLoading && <Spinner />}
		</>
	);
};

export default DeleteCard;
